import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useBranch } from '../context/BranchContext';
import { useAuth } from '../context/AuthContext';
import {
    todayStr,
    yesterdayStr,
    isTodayRange,
    getSalesSummary,
    getSalesByChannel,
    getPaymentDetails,
    getTenderTypes,
    getLiveRefunds,
    getLiveNetSales,
    getEodTaxesData,
    getEodReports,
    getEmployeeStaffTipGratuity,
    getByDuration,
    getCancellationSummary,
    getProductItemsCancelledReasons,
    getVoidedOrderSummary,
    getVoidedDetailsSummary,
    getPaidVoidedOrders,
} from '../services/reportsApi';
import { useStoreTimeZone, getStoreTimeZone } from '../utils/dateTimeUtils';
import { resolveTokenRange, normalizeToken, comparisonTooltip } from '../utils/reportPeriod';
import { getCurrencySymbol } from '../utils/currency';
import { combineTgDeltaPct, TG_CARD_LABEL } from '../components/reports/components/TipsGratuity';
import { getSnapshotDetails, getLiveByChannelApi, getLiveStaffPerformanceApi } from '../redux/reports/clickHouse/clickHouseApi';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrap = (res: any): any => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

/** If backend returns summary as array with one element, use first element; else use object as-is */
const unwrapSummary = (res: any): any => {
    const d = unwrap(res);
    if (Array.isArray(d) && d.length > 0) return d[0];
    return d && typeof d === 'object' && !Array.isArray(d) ? d : {};
};

const toNum = (v: any): number => (v != null && v !== '' ? Number(v) : 0);

// Delegates to the single GLOBAL-DP resolver (utils/reportPeriod). The long-range EOD
// presets (Last Month, Last 3/6 Months, This/Last Year) now live there too, so a range
// picked here resolves identically on every other report the shared cross-report filter
// carries it to — previously they resolved to null everywhere except EOD.
// Short-range presets stay anchored on the store-timezone anchor date (5 AM cutoff) —
// see getAnchorDate / DateProdIssue.md.
function getStartEndFromFilter(filter: string, today: string, yesterday: string, timeZoneCd: string = getStoreTimeZone()): { startDate: string; endDate: string } | null {
    if (normalizeToken(filter) === 'today') return { startDate: today, endDate: today };
    return resolveTokenRange(filter, timeZoneCd);
}

const TENDER_COLORS = ['#D23A3B', '#4A90D9', '#D97706', '#16A34A', '#8B5CF6', '#9CA3B8'];

/**
 * Build tender pie data from payment list. Merges rows with the same name (sum amounts and order counts); no rounding.
 * API shape: { paymentMode, cardType, cardName, totalSales, totalOrders, salesPercentage, tenderType, tenderTypeName }.
 * Output: [{ name, value (%), color, totalSales, orders }] for chart + breakdown (avg ticket = totalSales/orders in UI).
 */
function buildTenderData(paymentList: any): any[] {
    const list = Array.isArray(paymentList) ? paymentList : [];
    const total = list.reduce((sum, row) => sum + toNum(row.totalSales ?? row.amount ?? row.sales ?? row.total ?? row.value), 0);
    if (total <= 0) return [];
    const byName: Record<string, any> = {};
    list.forEach((row: any, i: number) => {
        const name = String(row.cardName ?? row.tenderTypeName ?? row.paymentMode ?? row.tenderType ?? row.channelName ?? row.name ?? `Tender ${i + 1}`).trim() || 'Other';
        const amt = toNum(row.totalSales ?? row.amount ?? row.sales ?? row.total ?? row.value);
        const orders = toNum(row.totalOrders ?? row.orderCount ?? row.orders ?? row.count ?? 0);
        if (!byName[name]) byName[name] = { name, amount: 0, orders: 0, color: TENDER_COLORS[Object.keys(byName).length % TENDER_COLORS.length] };
        byName[name].amount += amt;
        byName[name].orders += orders;
    });
    return Object.values(byName)
        .map((d) => ({
            name: d.name,
            value: total > 0 ? (d.amount / total) * 100 : 0,
            color: d.color,
            totalSales: d.amount,
            orders: d.orders,
        }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 12);
}

/**
 * Build channel chart data.
 * API shape: [{ channelName, sales, orders }] or with revenue_tag / maghil vs thirdParty.
 * Output: [{ channel, maghil, thirdParty, directSales, thirdPartySales, totalSales }].
 */
function buildChannelData(channelList: any): any[] {
    const isThirdPartyChannel = (name: any): boolean =>
        /third.?party|3p|uber|ubereats|door.?dash|grub.?hub|postmates|swiggy|zomato|deliveroo|just.?eat|talabat|foodpanda/i.test(String(name || ''));

    const list = Array.isArray(channelList) ? channelList : [];
    return list.map((row) => {
        const channel = row.channelName ?? row.channel ?? row.name ?? '—';
        const totalFromApi = toNum(row.totalSales ?? row.sales ?? row.amount ?? row.value ?? row.total ?? row.netSales);

        const directFromApi = toNum(
            row.directSales ??
            row.maghilSales ??
            row.magilSales ??
            row.maghil ??
            row.magil ??
            row.maghilTotalSale ??
            row.magilTotalSale
        );

        const thirdPartyFromApi = toNum(
            row.thirdPartySales ??
            row.thirdpartySales ??
            row.thirdPartyTotalSale ??
            row.thirdpartyTotalSale ??
            row.thirdPartyTotalSales ??
            row.thirdParty ??
            row.thirdparty
        );

        let maghil = directFromApi;
        let thirdParty = thirdPartyFromApi;

        // Some responses provide only one total per channel (sales/totalSales).
        // In that case infer split by known third-party channel names.
        if (maghil === 0 && thirdParty === 0 && totalFromApi > 0) {
            if (isThirdPartyChannel(channel)) thirdParty = totalFromApi;
            else maghil = totalFromApi;
        }

        const totalSales = totalFromApi > 0 ? totalFromApi : (maghil + thirdParty);
        return {
            channel,
            // Keep old keys for existing UI usage.
            maghil,
            thirdParty,
            // Add explicit names to mirror maghil-portal terminology.
            directSales: maghil,
            thirdPartySales: thirdParty,
            totalSales,
        };
    });
}

/**
 * Build funds table from EOD report rows.
 * API shape: content[] with date, maghilTotalSale, cashSale, otherSale, cardSale (processed/deposited if present).
 */
function buildFundsData(eodRows: any): any[] {
    const rows = Array.isArray(eodRows) ? eodRows : [];
    return rows.map((row) => {
        const date = row.reportDate ?? row.date ?? row.endDate ?? '—';
        const expected = toNum(row.maghilTotalSale) || (toNum(row.cashSale) + toNum(row.cardSale) + toNum(row.otherSale)) || toNum(row.expected);
        const processed = toNum(row.processed ?? row.processedFunds ?? expected);
        const deposited = toNum(row.deposited ?? row.depositedFunds ?? processed);
        return { date: String(date).slice(0, 10) || '—', expected, processed, deposited };
    });
}

/**
 * Build taxes table from getTaxesData response.
 * API shape: content[] with date, taxableSales, computedTax, collectedTax, difference.
 */
function buildTaxData(taxRows: any): any[] {
    const rows = Array.isArray(taxRows) ? taxRows : [];
    return rows.map((row) => ({
        date: row.reportDate ?? row.date ?? '—',
        taxableSales: toNum(row.taxableSales ?? row.taxable),
        computed: toNum(row.computedTax ?? row.computed),
        collected: toNum(row.collectedTax ?? row.collected),
        diff: toNum(row.difference ?? row.diff ?? 0),
    }));
}

/**
 * Build staff tips from staffTipAndGratuity.
 * API shape: content[] with employeeName, totalMagilTips, totalTipsPercentage, gratuity, totalMagilNetSales, etc.
 */
function buildStaffTips(staffList: any): any[] {
    const list = Array.isArray(staffList) ? staffList : [];
    return list.map((row) => {
        const pct = row.totalTipsPercentage ?? row.netSalesPercentage ?? row.tipRate ?? row.rate;
        const rawName = row.fullName ?? row.employeeName ?? row.staffName ?? row.name;
        const name = String(rawName ?? '—').trim() || '—';
        const serviceFee = toNum(row.serviceFee ?? row.gratuity ?? row.serviceCharge ?? 0);
        const orders = toNum(row.orders ?? row.orderCount ?? row.totalOrders ?? 0);
        return {
            name,
            tips: toNum(row.totalMagilTips ?? row.tip ?? row.tips ?? row.totalTip),
            rate: pct != null && pct !== '' ? `${Number(pct)}%` : '—',
            serviceFee,
            orders,
            // ZAR-1762 / TG-2 — the drill-in derives each staff member's individual tip rate
            // from their own sales, so carry the per-staff net sales through.
            netSales: toNum(row.totalMagilNetSales ?? row.netSales ?? row.totalNetSales ?? 0),
        };
    });
}

const REFUND_REASON_COLORS = ['#DC2626', '#D97706', '#D23A3B', '#9CA3B8', '#5A6178'];

/** Build refund reasons from voided summary, cancellation summary, or product insights. */
function buildRefundReasons(voidSummaryRows: any, cancelList: any, productCancelledReasons: any): any[] {
    const voidRows = Array.isArray(voidSummaryRows) ? voidSummaryRows : [];
    if (voidRows.length > 0) {
        const reasonMap: Record<string, any> = voidRows.reduce((acc: any, row: any) => {
            const reason = String(row.voidedReasons ?? row.reason ?? row.voidReason ?? 'Unknown').trim() || 'Unknown';
            if (!acc[reason]) acc[reason] = { amount: 0 };
            acc[reason].amount += toNum(row.voidedAmount ?? row.amount ?? row.totalAmount ?? 0);
            return acc;
        }, {});
        const totalAmount = Object.values(reasonMap).reduce((sum: number, row: any) => sum + row.amount, 0);
        if (totalAmount > 0) {
            return Object.entries(reasonMap)
                .map(([reason, value], i) => ({
                    reason,
                    amount: value.amount,
                    pct: Number(((value.amount / totalAmount) * 100).toFixed(1)),
                    color: REFUND_REASON_COLORS[i % REFUND_REASON_COLORS.length],
                }))
                .sort((a, b) => b.pct - a.pct);
        }
    }

    const list = Array.isArray(cancelList) ? cancelList : [];
    if (list.length > 0) {
        const reasonMap: Record<string, any> = list.reduce((acc: any, o: any) => {
            const r = String(o.reason ?? o.cancelReason ?? o.cancelledReason ?? 'Unknown').trim() || 'Unknown';
            if (!acc[r]) acc[r] = { count: 0, amount: 0 };
            acc[r].count += 1;
            acc[r].amount += toNum(o.amount ?? o.totalAmount ?? o.cancelAmount ?? 0);
            return acc;
        }, {});
        const total = Object.values(reasonMap).reduce((s: number, x: any) => s + x.count, 0) || 1;
        return Object.entries(reasonMap)
            .map(([reason, v], i) => ({ reason, pct: Math.round((v.count / total) * 100), color: REFUND_REASON_COLORS[i % REFUND_REASON_COLORS.length] }))
            .sort((a, b) => (b.pct - a.pct));
    }
    const reasonList = Array.isArray(productCancelledReasons) ? productCancelledReasons : (productCancelledReasons?.content ?? productCancelledReasons?.data ?? []);
    if (reasonList.length > 0) {
        const total = reasonList.reduce((s, x) => s + toNum(x.count ?? x.itemCount ?? x.quantity ?? 0), 0) || 1;
        return reasonList.slice(0, 8).map((row, i) => ({
            reason: row.reason ?? row.cancelReason ?? row.reasonDescription ?? row.categoryName ?? '—',
            pct: Math.round((toNum(row.count ?? row.itemCount ?? row.quantity) / total) * 100),
            color: REFUND_REASON_COLORS[i % REFUND_REASON_COLORS.length],
        }));
    }
    return [];
}

/** Build void log from paid voided orders (today) or voided details summary (date range). */
function buildVoidLog(paidVoidedOrders: any, voidDetails: any, isToday: boolean): any[] {
    if (isToday && paidVoidedOrders != null) {
        const list = Array.isArray(paidVoidedOrders) ? paidVoidedOrders : (paidVoidedOrders?.content ?? paidVoidedOrders?.data ?? []);
        return list.slice(0, 20).map((v) => ({
            staff: v.staffName ?? v.staff ?? v.employeeName ?? v.employee ?? '—',
            item: v.itemName ?? v.menuItemName ?? v.productName ?? v.item ?? v.name ?? v.orderItemName ?? (v.orderNumber ?? v.orderNo ? `#${v.orderNumber ?? v.orderNo}` : '—'),
            reason: v.reason ?? v.voidReason ?? v.reasonDescription ?? '—',
            amount: toNum(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.orderAmount ?? v.totalPrice),
            time: v.time ?? v.voidTime ?? v.date ?? v.voidDate ?? v.createdAt ?? '—',
            orderNumber: v.orderNumber ?? v.orderNo ?? v.orderId ?? v.ticketNumber ?? v.referenceNumber ?? '—',
            orderChannel: v.orderChannel ?? v.channel ?? v.orderSource ?? v.source ?? '—',
            orderType: v.orderType ?? v.type ?? v.diningType ?? v.serviceType ?? '—',
            customerName: v.customerName ?? v.nameOfCustomer ?? v.guestName ?? v.customer ?? '—',
            customerNumber: v.customerNumber ?? v.phoneNumber ?? v.mobileNumber ?? v.phone ?? '—',
        }));
    }
    const details = Array.isArray(voidDetails) ? voidDetails : (voidDetails?.content ?? voidDetails?.data ?? []);
    return details.slice(0, 20).map((v) => ({
        staff: v.staffName ?? v.staff ?? v.employeeName ?? v.employee ?? '—',
        item: v.itemName ?? v.menuItemName ?? v.productName ?? v.item ?? v.name ?? v.orderItemName ?? (v.orderNumber ?? v.orderNo ? `#${v.orderNumber ?? v.orderNo}` : '—'),
        reason: v.reason ?? v.voidReason ?? v.reasonDescription ?? '—',
        amount: toNum(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.orderAmount ?? v.totalPrice ?? 0),
        time: v.time ?? v.voidTime ?? v.voidDate ?? v.date ?? '—',
        orderNumber: v.orderNumber ?? v.orderNo ?? v.orderId ?? v.ticketNumber ?? v.referenceNumber ?? '—',
        orderChannel: v.orderChannel ?? v.channel ?? v.orderSource ?? v.source ?? '—',
        orderType: v.orderType ?? v.type ?? v.diningType ?? v.serviceType ?? '—',
        customerName: v.customerName ?? v.nameOfCustomer ?? v.guestName ?? v.customer ?? '—',
        customerNumber: v.customerNumber ?? v.phoneNumber ?? v.mobileNumber ?? v.phone ?? '—',
    }));
}

/** Build revenue trend from by-duration current array (daily) */
function buildRevenueOverTime(byDurationCurrent: any, summary: any): any[] {
    const current = Array.isArray(byDurationCurrent) ? byDurationCurrent : [];
    if (current.length > 0) {
        return current.map((d) => ({
            date: d.date ?? d.label ?? d.period ?? '—',
            gross: toNum(d.gross ?? d.grossSales ?? d.totalGross),
            net: toNum(d.net ?? d.netSales ?? d.totalNet),
        })).filter((r) => r.date !== '—');
    }
    const gross = toNum(summary?.totalGrossSalesIncludingThirdparty ?? summary?.grossSales);
    const net = toNum(summary?.totalNetSalesIncludingThirdparty ?? summary?.netSales);
    if (gross || net) {
        return [{ date: todayStr(), gross, net }];
    }
    return [];
}

/** Build waterfall items from summary */
function buildWaterfall(summary: any, overrides?: { gross?: number; net?: number; tips?: number; gratuity?: number }): any {
    const gross = overrides?.gross ?? toNum(summary?.totalGrossSalesIncludingThirdparty ?? summary?.grossSales ?? 0);
    const tax = toNum(summary?.magilTax ?? summary?.tax ?? 0);
    const thirdPartyTax = toNum(summary?.thirdPartyTax ?? 0);
    const discounts = toNum(summary?.discounts ?? 0);
    const refunds = toNum(summary?.cancelledOrders ?? summary?.refunds ?? 0);
    const processingFee = toNum(summary?.otherCharges ?? 0);
    const net = overrides?.net ?? toNum(summary?.totalNetSalesIncludingThirdparty ?? summary?.netSales ?? 0);
    const tips = overrides?.tips ?? toNum(summary?.totalTip ?? summary?.tips ?? 0);
    const gratuity = overrides?.gratuity ?? toNum(summary?.gratuity ?? summary?.totalGratuity ?? summary?.serviceCharge ?? 0);
    const totalDeposit = net + tips + gratuity;
    const items = [
        { label: 'Gross Sales', value: gross, type: 'positive' },
        { label: 'Sales Tax', value: -tax, type: 'deduction' },
        { label: 'Third-Party Tax', value: -thirdPartyTax, type: 'deduction' },
        // { label: 'Discounts', value: -discounts, type: 'deduction' },
        // { label: 'Refunds', value: -refunds, type: 'deduction' },
        { label: 'Processing Fee', value: -processingFee, type: 'deduction' },
        { label: 'Total Tips', value: -tips, type: 'deduction' },
        { label: 'Gratuity', value: -gratuity, type: 'deduction' },
        { label: 'Net Sales', value: net, type: 'net' },
    ];
    return { items, maxValue: gross || 1 };
}

const fmtKpi = (n: number): string => (n < 0 ? '-' : '') + getCurrencySymbol() + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Hook: EOD Summary data with live vs historical branching.
 * When date range is today (startDate === endDate === today), uses live APIs for tender, refunds, net sales.
 * @param {string} datePeriod - "Today" | "Yesterday" | "7D" | "30D" | "This Month"
 * @returns {{ data, loading, error, locationId, dateRange, isToday, refetch }}
 */
export function useEodSummaryData(datePeriod: string) {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? '', [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(datePeriod, today, yesterday, storeTz);
        return range ?? { startDate: today, endDate: today };
    }, [datePeriod, today, yesterday, storeTz]);
    // Read "today" off the resolved range so the date picker's today (`custom:<today>:<today>`)
    // takes the same live path as the Today pill — see isTodayRange (ZAR-1762).
    const isToday = useMemo(
        () => isTodayRange(dateRange.startDate, dateRange.endDate, today),
        [dateRange.startDate, dateRange.endDate, today],
    );

    const fetchIdRef = useRef(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [raw, setRaw] = useState<any>({
        summary: null,
        liveSummary: null,
        liveRefunds: null,
        channel: null,
        payment: null,
        taxes: null,
        eodReports: null,
        staffTips: null,
        byDuration: null,
        cancellationSummary: null,
        cancelledReasons: null,
        voidSummary: null,
        voidDetails: null,
        paidVoidedOrders: null,
        tgSnapshot: null,
        tgByChannel: null,
        tgStaff: null,
    });

    const fetchData = useCallback(() => {
        if (!locationId || !token) {
            setLoading(false);
            setRaw({});
            return;
        }
        const id = ++fetchIdRef.current;
        setLoading(true);
        setError(null);
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        const liveOpts = { locationId, token };
        // ClickHouse date params — omitted for Today (live path), passed only for historical
        // ranges. Matches Snapshot and Sales; see isTodayRange (ZAR-1762).
        const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const chOpts = { locationId, ...chDates, token };

        const allPromises = [
            getSalesSummary(opts).then(res => { console.log("[EOD] getSalesSummary →", res); return res; }).catch(err => { console.error("[EOD] getSalesSummary FAILED →", err); return null; }),
            getSalesByChannel(opts).catch(() => null),
            (isToday ? getTenderTypes(liveOpts) : getPaymentDetails({ ...opts, page: 0, size: 50 })).catch(() => null),
            getEodTaxesData({ ...opts, page: 0, size: 31 }).catch(() => null),
            getEodReports({ ...opts, page: 0, size: 31 }).catch(() => null),
            // ZAR-1762 / TG-2 — the drill-in ranks the full staff list in one scroll instead of
            // paging 10 at a time, so pull the whole roster in the initial fetch.
            // Dates follow the same rule as the other Tips & Gratuity feeds: omitted for Today
            // (however Today was selected — pill or date picker), passed for historical ranges.
            getEmployeeStaffTipGratuity({ ...chOpts, tablePageNo: 1, tableRecordLimit: 200 }).catch(() => null),
            getByDuration({ ...opts, type: 'day' }).catch(() => ({ current: [], compare: [] })),
            getCancellationSummary(opts).catch(() => null),
            getProductItemsCancelledReasons(opts).catch(() => null),
            getVoidedOrderSummary(opts).then(res => { console.log("[EOD] getVoidedOrderSummary →", res); return res; }).catch(err => { console.error("[EOD] getVoidedOrderSummary FAILED →", err); return null; }),
            getVoidedDetailsSummary({ ...opts, token }).catch(() => null),
            // ZAR-1762 / TG-2 — the Tips & Gratuity card and drill-in read the same three
            // ClickHouse endpoints, with the same params, that Snapshot and Sales read. No
            // `orderSource`: EOD has no source toggle, and its own Net Sales KPI is
            // totalNetSalesIncludingThirdparty, so the surface is not direct-only. Scoping only
            // these three calls to direct would make the drill disagree both with the page it
            // sits on and with the identical drill on Snapshot.
            // /sales/summary carries none of these figures — no order counts, no channel or
            // staff attribution — which is why the KPI reads ClickHouse instead.
            getSnapshotDetails(chOpts).catch(() => null),
            getLiveByChannelApi(chOpts).catch(() => null),
            getLiveStaffPerformanceApi(chOpts).catch(() => null),
        ];
        if (isToday) {
            allPromises.push(
                getLiveNetSales(liveOpts).then(res => { console.log("[EOD] getLiveNetSales →", res); return res; }).catch(err => { console.error("[EOD] getLiveNetSales FAILED →", err); return null; }),
                getLiveRefunds(liveOpts).then(res => { console.log("[EOD] getLiveRefunds →", res); return res; }).catch(err => { console.error("[EOD] getLiveRefunds FAILED →", err); return null; }),
                getPaidVoidedOrders({ ...liveOpts, page: 0, size: 50 }).then(res => { console.log("[EOD] getPaidVoidedOrders →", res); return res; }).catch(err => { console.error("[EOD] getPaidVoidedOrders FAILED →", err); return null; })
            );
        }

        Promise.all(allPromises)
            .then((results) => {
                if (id !== fetchIdRef.current) return; // stale fetch — discard
                const [summary, channel, payment, taxes, eodReports, staffTipsRes, byDuration, cancellationSummary, cancelledReasons, voidSummary, voidDetails, tgSnapshot, tgByChannel, tgStaff] = results.slice(0, 14);
                const liveSummary = isToday ? (results[14] ?? null) : null;
                const liveRefunds = isToday ? (results[15] ?? null) : null;
                const paidVoidedOrders = isToday ? (results[16] ?? null) : null;
                setRaw({
                    summary: summary ?? null,
                    liveSummary: isToday ? (liveSummary ?? null) : null,
                    liveRefunds: isToday ? (liveRefunds ?? null) : null,
                    channel,
                    payment,
                    taxes,
                    eodReports,
                    staffTips: staffTipsRes,
                    byDuration: byDuration ?? { current: [], compare: [] },
                    cancellationSummary: cancellationSummary ?? null,
                    cancelledReasons: cancelledReasons ?? null,
                    voidSummary: voidSummary ?? null,
                    voidDetails: voidDetails ?? null,
                    paidVoidedOrders: isToday ? (paidVoidedOrders ?? null) : null,
                    tgSnapshot: tgSnapshot ?? null,
                    tgByChannel: tgByChannel ?? null,
                    tgStaff: tgStaff ?? null,
                });
            })
            .catch((err) => { if (id === fetchIdRef.current) setError(err?.message ?? 'Failed to load EOD data'); })
            .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
    }, [locationId, token, dateRange.startDate, dateRange.endDate, isToday]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const STAFF_TIPS_PAGE_SIZE = 10;
    const loadStaffTipsPage = useCallback((page: number): Promise<{ staffTips: any[]; staffTipsPagination: any } | null> => {
        if (!locationId || !token) return Promise.resolve(null);
        // Same date rule as the initial fetch above — omitted for Today (pill or date picker),
        // passed for historical ranges — so paging can't disagree with page 1 (ZAR-1762).
        const chOpts = isToday
            ? { locationId, token }
            : { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        return getEmployeeStaffTipGratuity({ ...chOpts, tablePageNo: page, tableRecordLimit: STAFF_TIPS_PAGE_SIZE })
            .then((res) => {
                setRaw((prev) => ({ ...prev, staffTips: res ?? null }));
                if (!res || typeof res !== 'object') return null;
                // Use the requested `page` as the source of truth — the API response `number`
                // field is unreliable (some backends always return 0).
                const pageNumber = page;
                const totalPages = res.totalPages ?? 1;
                const rawList = Array.isArray(res) ? res : (res.content ?? []);
                return {
                    staffTips: buildStaffTips(Array.isArray(rawList) ? rawList : []),
                    staffTipsPagination: {
                        pageNumber,
                        totalPages,
                        totalElements: res.totalElements ?? 0,
                        size: res.size ?? 20,
                        first: pageNumber === 1,
                        last: pageNumber >= totalPages,
                    },
                };
            });
    }, [locationId, token, dateRange.startDate, dateRange.endDate, isToday]);

    const data = useMemo(() => {
        const summary = raw.summary;
        const liveSummary = raw.liveSummary;
        const liveRefunds = raw.liveRefunds;
        const s = unwrapSummary(summary);
        const ls = unwrapSummary(liveSummary);

        const gross = isToday && (ls?.totalSales != null || ls?.totalGrossSales != null)
            ? toNum(ls.totalSales ?? ls.totalGrossSales)
            : toNum(s.totalGrossSalesIncludingThirdparty ?? s.grossSales);
        const net = isToday && (ls?.totalNetSales != null)
            ? toNum(ls.totalNetSales)
            : toNum(s.totalNetSalesIncludingThirdparty ?? s.netSales);
        let refundAmount = toNum(s.cancelledOrders ?? s.refunds);
        if (isToday && liveRefunds != null) {
            const lr = unwrap(liveRefunds);
            if (Array.isArray(lr)) refundAmount = lr.reduce((sum, r) => sum + toNum(r.amount), 0);
            else refundAmount = toNum(lr?.totalRefunds ?? lr?.amount ?? refundAmount);
        }
        const tips = isToday && ls?.totalTip != null ? toNum(ls.totalTip) : toNum(s.totalTip ?? s.tips);

        // Void amount for "Voids inside Total Refunds" KPI (must be before kpiItems).
        const voidSummaryRaw = raw.voidSummary;
        const voidSummaryListForKpi = Array.isArray(voidSummaryRaw) ? voidSummaryRaw : (voidSummaryRaw?.content ?? voidSummaryRaw?.data ?? []);
        let voidAmount = 0;
        if (voidSummaryListForKpi.length > 0) {
            voidAmount = voidSummaryListForKpi.reduce((sum, row) => sum + toNum(row.voidedAmount ?? row.amount ?? row.totalAmount ?? 0), 0);
        }
        if (isToday && raw.paidVoidedOrders != null) {
            const paidList = Array.isArray(raw.paidVoidedOrders) ? raw.paidVoidedOrders : (raw.paidVoidedOrders?.content ?? raw.paidVoidedOrders?.data ?? []);
            const paidSum = paidList.reduce((sum, v) => sum + toNum(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.orderAmount ?? v.totalPrice ?? 0), 0);
            if (paidSum > 0) voidAmount = paidSum;
        }

        const parseDelta = (v: any) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };
        const dpNorm = String(datePeriod || '').trim().toLowerCase().replace(/\s+/g, '');
        const hideDelta = ['30d', 'thismonth', 'lastmonth', 'last3months', 'last6months', 'thisyear', 'lastyear'].includes(dpNorm) || dpNorm.startsWith('custom');

        const grossDelta = hideDelta ? undefined : parseDelta(s.totalSalesPercentage);
        const netDelta = hideDelta ? undefined : parseDelta(s.netSalesPercentage);
        const refundDelta = hideDelta ? undefined : parseDelta(s.cancelledAmtPercentage);
        const tipsDelta = hideDelta ? undefined : parseDelta(s.totalTipsPercentage);

        // ZAR-1762 / TG-1 — EOD's unified Tips & Gratuity card. Gratuity comes from the
        // summary when the API reports it and falls back to the sum of per-staff service fees.
        // EOD is direct-only: there is no source toggle and no 3rd-party gratuity here.
        // ZAR-1762 / TG-2 — everything the drill-in's two sections need, from ClickHouse scoped
        // to direct sales. One source for totals, denominators and the channel split, so the
        // card, the sub-header and both panels can never disagree with each other.
        // `/sales/summary` carries none of the counts and no channel attribution, so it stays
        // the fallback for the totals only (older deployments / a failed ClickHouse call).
        const tgSnap: any = unwrap(raw.tgSnapshot) ?? {};
        const tgChannelRaw = raw.tgByChannel;
        const tgChannels: any[] = Array.isArray(tgChannelRaw) ? tgChannelRaw : (tgChannelRaw?.content ?? unwrap(tgChannelRaw) ?? []);
        const tgStaffRaw = raw.tgStaff;
        const tgStaffRows: any[] = Array.isArray(tgStaffRaw) ? tgStaffRaw : (tgStaffRaw?.content ?? unwrap(tgStaffRaw) ?? []);
        // Null-check, not `||`: a genuine 0 (a period with no tips) is a real answer and must not
        // fall through to the summary's figure.
        const tgNum = (v: any, fallback: number) => (v == null ? fallback : toNum(v));

        const staffRawForTips = raw.staffTips;
        const staffListForTipsKpi = Array.isArray(staffRawForTips) ? staffRawForTips : (staffRawForTips?.content ?? unwrap(staffRawForTips) ?? []);
        const gratuityFromStaff = staffListForTipsKpi.reduce((sum, row) => sum + toNum(row.serviceFee ?? row.gratuity ?? row.serviceCharge ?? 0), 0);
        const tgTips = tgNum(tgSnap.tip, tips);
        const gratuityTotal = tgNum(tgSnap.gratuity, toNum(s.gratuity ?? s.totalGratuity ?? s.serviceCharge) || gratuityFromStaff);
        const gratuityDelta = hideDelta ? undefined : parseDelta(s.totalGratuityPercentage ?? s.gratuityPercentage);
        // ClickHouse computes the combined total itself; sum only if it is absent.
        const tipsAndGratuity = tgNum(tgSnap.tipAndGratuity, tgTips + gratuityTotal);
        const tipsGratuityDelta = combineTgDeltaPct(tips, tipsDelta ?? null, gratuityTotal, gratuityDelta ?? null) ?? undefined;

        // GLOBAL-DP-4 (ZAR-1663): the "vs Previous day" text is removed from the four hero
        // KPIs — the basis is stated once in the unified date control. What remains is the
        // resolved comparison period as a hover tooltip. Note the basis itself also moves
        // from the previous calendar day to the same weekday in the prior week (GLOBAL-DP-2),
        // which is a material change on a reconciliation surface and needs finance sign-off.
        const deltaLabel = comparisonTooltip(datePeriod || 'Yesterday', storeTz);

        const kpiItems = [
            { label: 'Gross Sales', value: fmtKpi(gross), delta: grossDelta, deltaLabel, icon: 'trending_up', iconColor: '#16A34A', blurColor: 'rgba(22,163,74,0.08)', drillKey: null, tooltip: 'Revenue deducting discounts, complimentary, voids & refunds. The amount paid by customers including tax, tips & other fees' },
            { label: 'Net Sales', value: fmtKpi(net), delta: netDelta, deltaLabel, icon: 'account_balance_wallet', iconColor: '#D23A3B', blurColor: 'rgba(210,58,59,0.08)', drillKey: null, tooltip: 'From Gross sales after deducting tax, tips & gratuity. The revenue retained by you after all deductions.' },
            { label: 'Total Refunds', value: fmtKpi(refundAmount), delta: refundDelta, deltaLabel, drillKey: 'refunds', icon: 'undo', iconColor: '#DC2626', blurColor: 'rgba(220,38,38,0.08)' },
            { label: TG_CARD_LABEL, value: fmtKpi(tipsAndGratuity), delta: tipsGratuityDelta, deltaLabel, drillKey: 'tips', icon: 'volunteer_activism', iconColor: '#16A34A', blurColor: 'rgba(22,163,74,0.08)', cardBg: 'rgba(22,163,74,0.08)' },
        ];

        const channelRaw = raw.channel;
        const channelList = Array.isArray(channelRaw) ? channelRaw : (channelRaw?.content ?? unwrap(channelRaw) ?? []);
        const channelData = buildChannelData(Array.isArray(channelList) ? channelList : []);
        const totalMaghil = channelData.reduce((s, r) => s + (r.maghil || 0), 0);
        const totalThirdParty = channelData.reduce((s, r) => s + (r.thirdParty || 0), 0);
        const channelTotal = totalMaghil + totalThirdParty;
        const channelSummary = {
            totalSales: channelTotal,
            directSales: totalMaghil,
            thirdPartySales: totalThirdParty,
            totalMaghil,
            totalThirdParty,
            maghilPct: channelTotal > 0 ? (totalMaghil / channelTotal) * 100 : 0,
        };

        const paymentRaw = raw.payment;
        const paymentList = Array.isArray(paymentRaw) ? paymentRaw : (paymentRaw?.content ?? unwrap(paymentRaw) ?? []);
        const tenderData = buildTenderData(Array.isArray(paymentList) ? paymentList : []);

        const taxesRaw = raw.taxes;
        const taxRows = Array.isArray(taxesRaw) ? taxesRaw : (taxesRaw?.content ?? unwrap(taxesRaw) ?? []);
        const taxData = buildTaxData(Array.isArray(taxRows) ? taxRows : []);

        const eodRaw = raw.eodReports;
        const eodRows = Array.isArray(eodRaw) ? eodRaw : (eodRaw?.content ?? unwrap(eodRaw) ?? []);
        console.log('[EOD] getEodReports (/eod/getEODReports, fundsData source):', JSON.stringify(eodRaw, null, 2));
        const fundsData = buildFundsData(Array.isArray(eodRows) ? eodRows : []);

        const staffRaw = raw.staffTips;
        const staffList = Array.isArray(staffRaw) ? staffRaw : (staffRaw?.content ?? unwrap(staffRaw) ?? []);
        const staffTips = buildStaffTips(Array.isArray(staffList) ? staffList : []);
        const staffTipsPagination = (() => {
            if (!staffRaw || typeof staffRaw !== 'object' || Array.isArray(staffRaw)) return null;
            // API returns 0-indexed page numbers in the response body; we use 1-indexed externally.
            const pageNumber = (staffRaw.number ?? staffRaw.pageNumber ?? 0) + 1;
            const totalPages = staffRaw.totalPages ?? 1;
            return {
                pageNumber,
                totalPages,
                totalElements: staffRaw.totalElements ?? 0,
                size: staffRaw.size ?? 20,
                first: staffRaw.first != null ? staffRaw.first : (pageNumber === 1),
                last: staffRaw.last != null ? staffRaw.last : (pageNumber >= totalPages),
            };
        })();

        const byDurationCurrent = raw.byDuration?.current ?? [];
        const revenueOverTime = buildRevenueOverTime(byDurationCurrent, s);

        const thirdPartyReconciliation = [];

        const cancelRaw = raw.cancellationSummary;
        const cancelList = Array.isArray(cancelRaw) ? cancelRaw : (cancelRaw?.content ?? cancelRaw?.data ?? []);
        const productReasonsRaw = raw.cancelledReasons;
        const productReasonsList = Array.isArray(productReasonsRaw) ? productReasonsRaw : (productReasonsRaw?.content ?? productReasonsRaw?.data ?? []);
        const voidSummaryRawForReasons = raw.voidSummary;
        const voidSummaryList = Array.isArray(voidSummaryRawForReasons) ? voidSummaryRawForReasons : (voidSummaryRawForReasons?.content ?? voidSummaryRawForReasons?.data ?? []);
        const refundReasons = buildRefundReasons(voidSummaryList, cancelList, productReasonsList);

        const voidCount = voidSummaryList.reduce((c, row) => c + toNum(row.orderCount ?? row.count ?? 0), 0);

        // Temporary: disable void mapping until dedicated void API contract is finalized.
        const voidLog = [];

        const staffListForTips = Array.isArray(staffRaw) ? staffRaw : (staffRaw?.content ?? unwrap(staffRaw) ?? []);
        const totalTipsFromStaff = staffListForTips.reduce((sum, row) => sum + toNum(row.totalMagilTips ?? row.tip ?? row.tips ?? row.totalTip), 0);
        const totalTipsVal = isToday && ls?.totalTip != null ? toNum(ls.totalTip) : toNum(s.totalTip ?? s.tips) || totalTipsFromStaff;
        const serviceChargeFromStaff = staffListForTips.reduce((sum, row) => sum + toNum(row.serviceFee ?? row.gratuity ?? row.serviceCharge ?? 0), 0);
        // Untouched by ZAR-1762 — the Sales-to-Funding waterfall reads this, so it keeps
        // sourcing from /sales/summary exactly as before.
        const tipsSummary = {
            totalTips: totalTipsVal,
            tipRate: net > 0 ? (totalTipsVal / net) * 100 : 0,
            serviceCharge: toNum(s.gratuity ?? s.totalGratuity ?? s.serviceCharge) || serviceChargeFromStaff,
        };

        // ZAR-1762 — feeds the Tips & Grat card and its drill-in, and nothing else. Same three
        // ClickHouse endpoints, same params and same response keys Snapshot and Sales use, so
        // the identical drill reads identically on both surfaces.
        const tipsGratuity = {
            totalTips: tgTips,
            gratuity: gratuityTotal,
            combined: tipsAndGratuity,
            // Same source as the totals above, so both rates divide by the Net Sales those
            // totals came from — and match the identical drill on Snapshot. EOD's summary figure
            // is only a fallback: it is 0 on outlets whose summary reports no net sales, which
            // would otherwise render both rates as "0.0%".
            netSales: tgNum(tgSnap.netSales, net),
            // Tips averages over ALL orders, Gratuity over gratuity-bearing orders only (TG-2).
            totalOrders: toNum(tgSnap.orderCount),
            ordersWithTips: toNum(tgSnap.ordersWithTips),
            ordersWithGratuity: toNum(tgSnap.ordersWithGratuity),
            channels: tgChannels.map((c: any) => ({
                channel: c?.channel ?? null,
                tip: toNum(c?.tip),
                gratuity: toNum(c?.gratuity),
            })),
            // Rankings from staff-perfomance — the feed Snapshot and Sales rank from, and the
            // only one carrying `staff_name`.
            staff: tgStaffRows.map((r: any) => ({
                name: r?.staff_name ?? '',
                tip: toNum(r?.tip),
                gratuity: toNum(r?.gratuity),
                orderCount: toNum(r?.orderCount),
                netSales: toNum(r?.netSales),
            })),
        };

        const { items: eodWaterfall, maxValue: waterfallMax } = buildWaterfall(s, {
            gross,
            net,
            tips: tipsSummary.totalTips,
            gratuity: tipsSummary.serviceCharge,
        });

        const refundsSummary = {
            refundAmount,
            refundRate: gross > 0 ? (refundAmount / gross) * 100 : 0,
            voidAmount: voidAmount > 0 ? voidAmount : null,
            voidCount,
        };

        return {
            kpiItems,
            revenueOverTime,
            tenderData,
            channelData,
            channelSummary,
            eodWaterfall,
            waterfallMax,
            fundsData,
            taxData,
            staffTips,
            staffTipsPagination,
            thirdPartyReconciliation,
            refundReasons,
            voidLog,
            tipsSummary,
            tipsGratuity,
            refundsSummary,
        };
    }, [raw, isToday]);

    console.log("useEodSummaryData →", data);

    return {
        data,
        loading,
        error,
        locationId,
        dateRange,
        isToday,
        refetch: fetchData,
        loadStaffTipsPage,
    };
}
